// Чистая логика тренировки в зале: заготовка подходов из плана или прошлого раза, копия
// предыдущего подхода, запросы на сервер. Состояние живёт в offline/activeSession.

import { DEFAULT_REST_S } from '@/lib/restTimer'
import type { ActiveExercise, ActiveSet, ActiveWorkout } from '@/offline/activeSession'
import type { EditItem, SessionCheckRequest, WorkoutDraft } from '@/pages/ai/api'
import type { Prescription } from '@/pages/plan/api'
import type { Session, SessionInput, SetInput, TargetSet, Template, Tracking, WorkoutSet } from '@/pages/workouts/api'
import { groupSets, summarizeSets } from '@/pages/workouts/logic'

/** Шаг веса кнопками ±, кг. */
export const WEIGHT_STEP = 2.5

type SetValues = Pick<ActiveSet, 'weight_kg' | 'reps' | 'duration_s' | 'distance_m' | 'rest_s'>

const EMPTY_VALUES: SetValues = { weight_kg: null, reps: null, duration_s: null, distance_m: null, rest_s: null }

export function newId(): string {
  return crypto.randomUUID()
}

function newSet(values: SetValues, setIndex: number): ActiveSet {
  return {
    id: newId(),
    set_index: setIndex,
    ...values,
    is_warmup: false,
    completed: false,
    completed_at: null,
    saved: false,
  }
}

const working = (sets: WorkoutSet[]) => sets.filter((s) => s.completed && !s.is_warmup)

/** «3 × 8 × 80 кг» — рабочие подходы прошлого раза; null, если не делал. */
export function lastSummary(last: WorkoutSet[], tracking: Tracking): string | null {
  const sets = working(last)
  return sets.length ? summarizeSets(sets, tracking) : null
}

export type ExerciseSource = {
  exercise_id: string
  name: string
  tracking: Tracking
  position: number
  /** Целевые подходы плана или шаблона (может быть пусто). */
  targets: TargetSet[]
  /** Подходы прошлой тренировки с этим упражнением. */
  last: WorkoutSet[]
  reason?: string | null
  warning?: string | null
}

/**
 * Упражнение с заготовкой подходов: по одному на каждую цель плана; чего нет в цели (вес,
 * повторы) — из прошлого раза. Без плана — один подход как первый рабочий прошлого раза.
 */
export function buildExercise(source: ExerciseSource): ActiveExercise {
  const prev = working(source.last)
  const prevAt = (i: number): WorkoutSet | undefined => prev[i] ?? prev[prev.length - 1]
  const restOfTemplate = source.targets.find((t) => t.rest_s !== null)?.rest_s ?? null

  let values: SetValues[]
  if (source.targets.length > 0) {
    values = source.targets.map((t, i) => {
      const p = prevAt(i)
      return {
        weight_kg: t.weight_kg ?? p?.weight_kg ?? null,
        reps: t.reps ?? p?.reps ?? null,
        duration_s: t.duration_s ?? p?.duration_s ?? null,
        distance_m: t.distance_m ?? p?.distance_m ?? null,
        rest_s: t.rest_s ?? restOfTemplate,
      }
    })
  } else {
    const p = prev[0]
    values = [
      p
        ? { weight_kg: p.weight_kg, reps: p.reps, duration_s: p.duration_s, distance_m: p.distance_m, rest_s: null }
        : EMPTY_VALUES,
    ]
  }

  return {
    key: newId(),
    exercise_id: source.exercise_id,
    name: source.name,
    tracking: source.tracking,
    position: source.position,
    rest_s: restOfTemplate,
    last_summary: lastSummary(source.last, source.tracking),
    reason: source.reason ?? null,
    warning: source.warning ?? null,
    seed: values[0] ?? null,
    sets: values.map((v, i) => newSet(v, i)),
  }
}

/** Новый подход: копия предыдущего подхода упражнения; если подходов нет — из плана/прошлого раза. */
export function nextSet(exercise: ActiveExercise): ActiveSet {
  const prev = exercise.sets[exercise.sets.length - 1]
  const index = exercise.sets.reduce((max, s) => Math.max(max, s.set_index + 1), 0)
  const values: SetValues = prev
    ? {
        weight_kg: prev.weight_kg,
        reps: prev.reps,
        duration_s: prev.duration_s,
        distance_m: prev.distance_m,
        rest_s: prev.rest_s,
      }
    : (exercise.seed ?? EMPTY_VALUES)
  return newSet(values, index)
}

/** Сколько отдыхать после подхода: из подхода, из шаблона, иначе 90 с. */
export function restSeconds(exercise: ActiveExercise, set: ActiveSet): number {
  return set.rest_s ?? exercise.rest_s ?? DEFAULT_REST_S
}

export function nextPosition(workout: ActiveWorkout): number {
  return workout.exercises.reduce((max, e) => Math.max(max, e.position + 1), 0)
}

/**
 * Номера упражнений (exercise_position) по порядку на экране: те же номера, что были, по
 * возрастанию — чтобы и на сервере упражнения шли в этом порядке. Меняются только те, что
 * встали не на своё место.
 */
export function renumberExercises(order: ActiveExercise[]): ActiveExercise[] {
  const positions = order.map((e) => e.position).sort((a, b) => a - b)
  return order.map((e, i) => (e.position === positions[i] ? e : { ...e, position: positions[i]! }))
}

/**
 * Цели для замены упражнения: столько же подходов, сколько не выполнено (хотя бы один), с тем
 * же отдыхом. Вес у другого упражнения свой — его возьмёт `buildExercise` из прошлого раза;
 * повторы, время и дистанция остаются, только если способ учёта тот же.
 */
export function replacementTargets(old: ActiveExercise, tracking: Tracking): TargetSet[] {
  const left = old.sets.filter((s) => !s.completed)
  const same = old.tracking === tracking
  const sets = left.length ? left : [old.sets.at(-1) ?? null]
  return sets.map((s) => ({
    weight_kg: null,
    reps: same ? (s?.reps ?? null) : null,
    duration_s: same ? (s?.duration_s ?? null) : null,
    distance_m: same ? (s?.distance_m ?? null) : null,
    rest_s: s?.rest_s ?? old.rest_s,
  }))
}

export function doneCount(workout: ActiveWorkout): number {
  return workout.exercises.reduce((n, e) => n + e.sets.filter((s) => s.completed).length, 0)
}

/** Сколько секунд тренировка стояла на паузе, включая текущую. */
export function pausedSeconds(workout: ActiveWorkout, now: number): number {
  const current = workout.paused_at ? Math.max(0, now - new Date(workout.paused_at).getTime()) / 1000 : 0
  return Math.round((workout.paused_s ?? 0) + current)
}

/** Сколько идёт тренировка без пауз, секунд; на паузе время стоит. */
export function workoutElapsed(workout: ActiveWorkout, now: number): number {
  return Math.max(0, (now - new Date(workout.started_at).getTime()) / 1000 - pausedSeconds(workout, now))
}

/** Упражнения «5 из 12»: завершённым считается то, где все подходы выполнены. */
export function exerciseProgress(workout: ActiveWorkout): { done: number; total: number } {
  const done = workout.exercises.filter((e) => e.sets.length > 0 && e.sets.every((s) => s.completed)).length
  return { done, total: workout.exercises.length }
}

type LastLookup = (exerciseId: string) => WorkoutSet[]

type StartOptions = { now?: Date; id?: string }

function shell(options: StartOptions, fields: Partial<ActiveWorkout>): ActiveWorkout {
  return {
    id: options.id ?? newId(),
    started_at: (options.now ?? new Date()).toISOString(),
    template_id: null,
    template_name: null,
    planned_session_id: null,
    notes: null,
    exercises: [],
    ...fields,
  }
}

export function emptyWorkout(options: StartOptions = {}): ActiveWorkout {
  return shell(options, {})
}

export function workoutFromTemplate(template: Template, last: LastLookup, options: StartOptions = {}): ActiveWorkout {
  return shell(options, {
    template_id: template.id,
    template_name: template.name,
    exercises: template.exercises
      .filter((e) => !e.exercise_archived)
      .map((e, i) =>
        buildExercise({
          exercise_id: e.exercise_id,
          name: e.exercise_name,
          tracking: e.tracking,
          position: i,
          targets: e.target_sets,
          last: last(e.exercise_id),
        }),
      ),
  })
}

export function workoutFromPrescription(
  prescription: Prescription,
  plannedId: string,
  last: LastLookup,
  options: StartOptions = {},
): ActiveWorkout {
  return shell(options, {
    template_id: prescription.template_id,
    template_name: prescription.template_name,
    planned_session_id: plannedId,
    exercises: [...prescription.exercises]
      .sort((a, b) => a.position - b.position)
      .map((e, i) =>
        buildExercise({
          exercise_id: e.exercise_id,
          name: e.exercise_name,
          tracking: e.tracking as Tracking,
          position: i,
          targets: e.sets,
          last: last(e.exercise_id),
          reason: e.reason,
          warning: e.injury_warning,
        }),
      ),
  })
}

/** Цель от ИИ («3 × 12, отдых 90 с») → одинаковые целевые подходы. */
export function aiTargets(e: {
  sets: number
  reps: number | null
  weight_kg: number | null
  duration_s: number | null
  distance_m: number | null
  rest_s: number | null
}): TargetSet[] {
  const target: TargetSet = {
    reps: e.reps,
    weight_kg: e.weight_kg,
    duration_s: e.duration_s,
    distance_m: e.distance_m,
    rest_s: e.rest_s,
  }
  return Array.from({ length: e.sets }, () => ({ ...target }))
}

/** Упражнения тренировки — для правки с ИИ; где есть выполненные подходы — `locked`. */
export function toEditItems(workout: ActiveWorkout): EditItem[] {
  return workout.exercises.map((e) => ({
    key: e.key,
    exercise_id: e.exercise_id,
    name: e.name,
    tracking: e.tracking,
    sets: e.sets.map((s) => ({
      reps: s.reps,
      weight_kg: s.weight_kg,
      duration_s: s.duration_s,
      distance_m: s.distance_m,
      rest_s: s.rest_s,
    })),
    locked: e.sets.some((s) => s.completed),
  }))
}

/**
 * Разовая тренировка от ИИ: заголовок — название от ИИ, подсказка по технике — под упражнением;
 * чего нет в цели (вес) — из прошлого раза. Упражнения не из справочника сюда уже не попадают.
 */
export function workoutFromAi(draft: WorkoutDraft, last: LastLookup, options: StartOptions = {}): ActiveWorkout {
  const known = draft.exercises.filter((e) => e.exercise_id !== null)
  return shell(options, {
    template_name: draft.title,
    notes: `${draft.title} — собрана с ИИ`,
    exercises: known.map((e, i) =>
      buildExercise({
        exercise_id: e.exercise_id!,
        name: e.exercise_name ?? e.name,
        tracking: (e.tracking ?? 'weight_reps') as Tracking,
        position: i,
        targets: aiTargets(e),
        last: last(e.exercise_id!),
        reason: e.notes,
      }),
    ),
  })
}

/** Незавершённая тренировка с сервера (начата на другом устройстве) → тренировка этого устройства. */
export function workoutFromSession(session: Session): ActiveWorkout {
  return {
    id: session.id,
    started_at: session.started_at,
    template_id: session.template_id,
    template_name: session.template_name,
    planned_session_id: session.planned_session_id,
    notes: session.notes,
    exercises: groupSets(session).map((g) => {
      const sets: ActiveSet[] = g.sets.map((s) => ({
        id: s.id,
        set_index: s.set_index,
        weight_kg: s.weight_kg,
        reps: s.reps,
        duration_s: s.duration_s,
        distance_m: s.distance_m,
        is_warmup: s.is_warmup,
        completed: s.completed,
        completed_at: s.completed_at,
        rest_s: s.rest_s,
        saved: true,
      }))
      const first = sets[0]
      return {
        key: newId(),
        exercise_id: g.exercise_id,
        name: g.name,
        tracking: g.tracking,
        position: g.position,
        rest_s: null,
        last_summary: null,
        reason: null,
        warning: null,
        seed: first
          ? { weight_kg: first.weight_kg, reps: first.reps, duration_s: first.duration_s, distance_m: first.distance_m, rest_s: first.rest_s }
          : null,
        sets,
      }
    }),
  }
}

/** Идущая тренировка с устройства — для «Совета ИИ» (хватит или добавить). */
export function checkRequest(workout: ActiveWorkout): SessionCheckRequest {
  return {
    title: workout.template_name,
    started_at: workout.started_at,
    exercises: workout.exercises.map((e) => ({
      exercise_id: e.exercise_id,
      name: e.name,
      tracking: e.tracking,
      reason: e.reason,
      sets: e.sets.map((s) => ({
        reps: s.reps === null ? null : Math.round(s.reps),
        weight_kg: s.weight_kg,
        duration_s: s.duration_s === null ? null : Math.round(s.duration_s),
        distance_m: s.distance_m,
        completed: s.completed,
        is_warmup: s.is_warmup,
      })),
    })),
  }
}

// --- Запросы ---------------------------------------------------------------------------------

export function sessionUrl(workout: Pick<ActiveWorkout, 'id'>): string {
  return `/api/sessions/${workout.id}`
}

export function setUrl(workout: Pick<ActiveWorkout, 'id'>, set: Pick<ActiveSet, 'id'>): string {
  return `/api/sessions/${workout.id}/sets/${set.id}`
}

/** Тело PUT тренировки: без окончания (завершает только POST /finish). */
export function sessionPayload(workout: ActiveWorkout): SessionInput & { planned_session_id: string | null } {
  return {
    started_at: workout.started_at,
    ended_at: null,
    template_id: workout.template_id,
    planned_session_id: workout.planned_session_id,
    notes: workout.notes,
  }
}

export function setPayload(exercise: ActiveExercise, set: ActiveSet): SetInput {
  return {
    exercise_id: exercise.exercise_id,
    exercise_position: exercise.position,
    set_index: set.set_index,
    reps: set.reps === null ? null : Math.round(set.reps),
    weight_kg: set.weight_kg,
    duration_s: set.duration_s === null ? null : Math.round(set.duration_s),
    distance_m: set.distance_m,
    is_warmup: set.is_warmup,
    completed: set.completed,
    completed_at: set.completed ? set.completed_at : null,
    rest_s: set.rest_s,
  }
}

export type WorkoutMuscle = { role: 'primary' | 'secondary'; done: number; total: number }

/**
 * Группы мышц текущей тренировки по справочнику: роль (основная, если хоть где-то основная),
 * рабочие подходы — выполненные и всего. Разминка не считается; упражнений вне справочника нет.
 */
export function workoutMuscles(
  workout: ActiveWorkout,
  catalog: ReadonlyMap<string, { primary_muscle: string; secondary_muscles: readonly string[] }>,
): Record<string, WorkoutMuscle> {
  const out: Record<string, WorkoutMuscle> = {}
  for (const exercise of workout.exercises) {
    const info = catalog.get(exercise.exercise_id)
    if (!info) continue
    const working = exercise.sets.filter((s) => !s.is_warmup)
    const done = working.filter((s) => s.completed).length
    const groups = new Set([info.primary_muscle, ...info.secondary_muscles])
    for (const group of groups) {
      const role = group === info.primary_muscle ? 'primary' : 'secondary'
      const item = (out[group] ??= { role, done: 0, total: 0 })
      if (role === 'primary') item.role = 'primary'
      item.done += done
      item.total += working.length
    }
  }
  return out
}
